import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { IUserSessionData, UserState, UserType } from '../../../../store/models/user.model';
import { EReducers, IReducers } from '../../../../store/reducers';
import { Store } from '@ngrx/store';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { EventService } from '../../../../store/services/event.service';
import { IToggleLeftMenuPayload, TOGGLE_LEFT_MENU_TYPE } from '../../../../store/models/page.model';
import {
  IReportEvent,
  IReportWsTopic,
  REPORT_EVENT_NAME,
  REPORT_WS_TOPIC
} from '../../../../reports/list-reports/list-reports.component';
import { WebSocketService } from '../../../../store/services/web-socket.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Button, ButtonColor, ButtonSize } from '../../../../store/models/button.model';
import { ContactsService } from '../../../../store/services/contacts.service';
import { openDialog, openSnackbar } from '../../../../store/actions/page.action';
import Timeout = NodeJS.Timeout;

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;

export const REPORT_WAIT_INTERVAL = 3 * SECONDS;
export const REPORT_CHECK_INTERVAL = 2 * MINUTES;


@Component({
  selector: 'app-authenticated-header',
  templateUrl: './authenticated-header.component.html',
  styleUrls: ['./authenticated-header.component.scss'],
})
export class AuthenticatedHeaderComponent implements OnInit, OnDestroy {
  userSession: IUserSessionData;
  showReportInformation: boolean;
  reportEventData: IReportEvent;
  wsWaitTimeout: Timeout;
  wsCheckInterval: Timeout;
  downloadReportButton: Button = {
    text: 'Download Report',
    color: ButtonColor.GREEN,
    size: ButtonSize.NORMAL,
    onClick: () => this.downloadReport()
  };

  readonly bars = faBars;

  private leftMenuOpened: boolean;
  private webSocketProcessed: boolean;

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store<IReducers>,
    private readonly eventService: EventService,
    private readonly wsService: WebSocketService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly contactsService: ContactsService
  ) {
  }

  ngOnInit() {
    this.store.select(EReducers.USER)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userState: UserState) => {
        if (userState.sessionData && userState.loggedIn) {
          this.userSession = userState.sessionData;
          const currentReport = localStorage.getItem(REPORT_EVENT_NAME);

          if (currentReport) {
            this.reportEventData = JSON.parse(currentReport);

            if (this.reportEventData.pushTo === this.userSession.id) {
              this.showReportInformation = true;
            }

            if(!this.reportEventData.completed) {
              this.wsWaitTimeout = setTimeout(this.waitForSocket.bind(this), REPORT_WAIT_INTERVAL);
            }
          }
        }
      });
    this.eventService.on<IToggleLeftMenuPayload>(TOGGLE_LEFT_MENU_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(menuState => this.leftMenuOpened = menuState.opened);
    this.eventService.on<IReportEvent>(REPORT_EVENT_NAME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (typeof data === 'boolean') {
          return;
        }
        this.showReportInformation = true;
        this.reportEventData = data;
        this.webSocketProcessed = false;
        this.changeDetectorRef.detectChanges();
      });

    this.wsService.on<IReportWsTopic>(REPORT_WS_TOPIC)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        const currentReport = localStorage.getItem(REPORT_EVENT_NAME);
        if (!currentReport) {
          return;
        }
        if (this.webSocketProcessed) {
          return;
        }

        if (data.pushTo !== this.userSession.id) {
          return;
        }

        if (!data.keyId || data.keyId !== this.reportEventData.keyId) {
          return;
        }

        this.webSocketProcessed = true;
        if (data.result === null) {
          localStorage.removeItem(REPORT_EVENT_NAME);
          this.showReportInformation = false;
          this.changeDetectorRef.detectChanges();
          return;
        } 


        this.reportEventData = {
          completed: true,
          result: data.result,
          file: data.file,
          message: data.message,
          pushTo: data.pushTo,
          keyId: data.keyId
        };

        if (data.result === 'error') {
          localStorage.removeItem(REPORT_EVENT_NAME);
          this.eventService.broadcast(REPORT_EVENT_NAME, false);
          this.showReportInformation = false;
          this.changeDetectorRef.detectChanges();
          this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: data.message,
                type: 'error'
              },
              duration: 5000
            }
          }));
          return;
        }
        this.eventService.broadcast(REPORT_EVENT_NAME, false);
        localStorage.setItem(REPORT_EVENT_NAME, JSON.stringify(this.reportEventData));
        this.changeDetectorRef.detectChanges();
      });
  }

  waitForSocket() {
    if (!this.webSocketProcessed) {
      this.checkForReport();
      this.wsCheckInterval = setInterval(this.checkForReport.bind(this), REPORT_CHECK_INTERVAL);
    }
  }

  checkForReport() {
    if (this.webSocketProcessed && this.wsCheckInterval) {
      clearInterval(this.wsCheckInterval);
      return;
    }
    this.wsService.send({
      topic: 'report',
      data: {
        action: 'check',
        keyId: this.reportEventData.keyId,
      }
    });
  }

  toggleLeftMenu() {
    this.leftMenuOpened = !this.leftMenuOpened;
    this.eventService.broadcast<IToggleLeftMenuPayload>(TOGGLE_LEFT_MENU_TYPE, {
      opened: this.leftMenuOpened,
    });
  }

  get showUpgradeButton(): boolean {
    return this.userSession?.user_type === UserType.SINGLE_USER
      && !this.userSession.vsm_group_id;
  }

  openUpgradeDialog(): void {
    this.store.dispatch(openDialog({
      dialogData: {
        title: 'Upgrade',
        content: '<strong>You focus on sales. We’ll push the buttons.</strong><br><br>'
          + 'With Retreva Lite, you run the show. Upgrade to Retreva, and our team handles your red emails, '
          + 'cycles, follow-ups, inactives, and removes—for less of a price jump than you might think.'
          + '<br><br>Want to learn more? Email <a href="mailto:help@retreva.com">help@retreva.com</a>.',
        canClose: true,
        dialogMinWidth: '520px',
      },
    }));
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private downloadReport() {
    this.contactsService.downloadMoveContactsTemplate('file?path=' + this.reportEventData.file + "&key=" + this.reportEventData.keyId)
      .subscribe(res => {
        const url = window.URL.createObjectURL(res.body);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.headers.get('Content-Disposition')
          .split(';')
          .map(f => f.trim())
          .filter(f => f.indexOf('filename=') !== -1)[0]
          .split('=')[1]
          .replace(new RegExp('"', 'g'), '');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        localStorage.removeItem(REPORT_EVENT_NAME);
        this.showReportInformation = false;
        this.reportEventData = null;
        this.eventService.broadcast(REPORT_EVENT_NAME, false);
        this.changeDetectorRef.detectChanges();
      }, err => {
        this.store.dispatch(openSnackbar({
          snackbarData: {
            data: {
              message: 'There was an error downloading the report. Please try again later.',
              type: 'error'
            },
            duration: 5000
          }
        }));

        this.showReportInformation = false;
        this.reportEventData = null;
        localStorage.removeItem(REPORT_EVENT_NAME);
        this.eventService.broadcast(REPORT_EVENT_NAME, false);
        this.changeDetectorRef.detectChanges();
      });
  }
}
